import { useLingui } from "@lingui/react/macro";
import { CheckIcon, CopyIcon, XIcon } from "lucide-react";
import {
  type AnchorHTMLAttributes,
  type MouseEvent as ReactMouseEvent,
  type ReactNode,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from "react";

import { useBasaDocumentDialogStore } from "../../basaDocumentDialogStore";
import { Button } from "../ui/button";
import { Dialog, DialogClose, DialogPopup, DialogTitle } from "../ui/dialog";
import {
  findQuoteRangesInSegments,
  type SegmentRange,
  type TextSegment,
} from "./BasaDocumentDialog.logic";

const QUOTE_HIGHLIGHT = "basa-quote";
const BLOCK_SELECTOR = "div, p, td, li";

interface RenderedDocument {
  readonly title: string;
  readonly elements: ReadonlyArray<HTMLElement>;
}

type LoadState =
  | { readonly status: "loading" }
  | { readonly status: "failed"; readonly httpStatus: number | null }
  | { readonly status: "ready"; readonly document: RenderedDocument };

function highlightRegistry(): HighlightRegistry | null {
  return typeof CSS !== "undefined" && "highlights" in CSS ? CSS.highlights : null;
}

function rtfUrl(href: string): string {
  const url = new URL(href, window.location.origin);
  url.searchParams.set("format", "rtf");
  return `${url.pathname}${url.search}`;
}

const fontSizeOf = (element: Element | null): string | null =>
  element instanceof HTMLElement && Number.parseFloat(element.style.fontSize) >= 1
    ? element.style.fontSize
    : null;

function restoreCollapsedFontSizes(root: HTMLElement) {
  for (const span of root.querySelectorAll<HTMLElement>("span")) {
    if (!span.style.fontSize || fontSizeOf(span) || !span.textContent?.trim()) continue;
    let previous = span.previousElementSibling;
    while (previous && !fontSizeOf(previous)) previous = previous.previousElementSibling;
    const size = fontSizeOf(previous);
    if (size) span.style.fontSize = size;
    else span.style.removeProperty("font-size");
  }
}

async function renderRtf(buffer: ArrayBuffer): Promise<RenderedDocument> {
  const { RTFJS } = await import("rtf.js");
  RTFJS.loggingEnabled(false);
  const document = new RTFJS.Document(buffer, {
    onPicture: (isLegacy, create) => {
      if (isLegacy) return window.document.createElement("span");
      try {
        return create();
      } catch {
        return window.document.createElement("span");
      }
    },
  });
  const title = document.metadata().title;
  const elements = await document.render();
  for (const element of elements) {
    restoreCollapsedFontSizes(element);
    for (const node of [element, ...element.querySelectorAll<HTMLElement>("*")]) {
      node.style.removeProperty("color");
      node.style.removeProperty("background-color");
    }
    for (const link of element.querySelectorAll("a")) {
      if (!/^https?:\/\//i.test(link.getAttribute("href") ?? "")) {
        link.replaceWith(...link.childNodes);
        continue;
      }
      link.target = "_blank";
      link.rel = "noopener noreferrer";
    }
  }
  return { title: typeof title === "string" ? title.trim() : "", elements };
}

function collectCardQuotes(anchor: HTMLElement): string[] {
  const root = anchor.closest(".chat-markdown");
  let block: Element = anchor;
  while (block.parentElement && block.parentElement !== root) {
    block = block.parentElement;
  }
  if (!root || block.parentElement !== root) return [];
  const quotes: string[] = [];
  for (
    let sibling = block.previousElementSibling;
    sibling && sibling.tagName !== "HR";
    sibling = sibling.previousElementSibling
  ) {
    if (sibling.tagName !== "BLOCKQUOTE") continue;
    const paragraphs = [...sibling.querySelectorAll("p")].map((p) => p.textContent ?? "");
    quotes.unshift(...(paragraphs.length > 0 ? paragraphs : [sibling.textContent ?? ""]));
  }
  return quotes.filter((quote) => quote.trim().length > 0);
}

function textSegments(root: HTMLElement): { nodes: Text[]; segments: TextSegment[] } {
  const nodes: Text[] = [];
  const segments: TextSegment[] = [];
  let previousBlock: Element | null = null;
  const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT);
  for (let node = walker.nextNode(); node; node = walker.nextNode()) {
    if (!(node instanceof Text) || node.length === 0) continue;
    const block = node.parentElement?.closest(BLOCK_SELECTOR) ?? null;
    nodes.push(node);
    segments.push({ text: node.data, startsBlock: block !== previousBlock });
    previousBlock = block;
  }
  return { nodes, segments };
}

function toDomRange(nodes: ReadonlyArray<Text>, range: SegmentRange): Range | null {
  const start = nodes[range.start.segment];
  const end = nodes[range.end.segment];
  if (!start || !end) return null;
  const domRange = document.createRange();
  domRange.setStart(start, Math.min(range.start.offset, start.length));
  domRange.setEnd(end, Math.min(range.end.offset, end.length));
  return domRange;
}

function BasaDocumentView({
  href,
  quotes,
}: {
  readonly href: string;
  readonly quotes: ReadonlyArray<string>;
}) {
  const { t } = useLingui();
  const [state, setState] = useState<LoadState>({ status: "loading" });
  const [attempt, setAttempt] = useState(0);
  const [copied, setCopied] = useState(false);
  const contentRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const controller = new AbortController();
    setState({ status: "loading" });
    fetch(rtfUrl(href), { credentials: "include", signal: controller.signal })
      .then(async (response) => {
        if (!response.ok) {
          setState({ status: "failed", httpStatus: response.status });
          return;
        }
        const document = await renderRtf(await response.arrayBuffer());
        if (!controller.signal.aborted) setState({ status: "ready", document });
      })
      .catch(() => {
        if (!controller.signal.aborted) setState({ status: "failed", httpStatus: null });
      });
    return () => controller.abort();
  }, [href, attempt]);

  useLayoutEffect(() => {
    const root = contentRef.current;
    if (state.status !== "ready" || !root) return;
    root.replaceChildren(...state.document.elements);
    const { nodes, segments } = textSegments(root);
    const ranges = findQuoteRangesInSegments(segments, quotes).flatMap((range) => {
      const domRange = toDomRange(nodes, range);
      return domRange ? [domRange] : [];
    });
    const registry = highlightRegistry();
    if (registry && ranges.length > 0) registry.set(QUOTE_HIGHLIGHT, new Highlight(...ranges));
    ranges[0]?.startContainer.parentElement?.scrollIntoView({ block: "center" });
    return () => {
      registry?.delete(QUOTE_HIGHLIGHT);
    };
  }, [state, quotes]);

  const loaded = state.status === "ready" ? state.document : null;

  const copyText = () => {
    const text = contentRef.current?.innerText;
    if (!text) return;
    void navigator.clipboard.writeText(text).then(() => {
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1500);
    });
  };

  const failureMessage =
    state.status !== "failed"
      ? null
      : state.httpStatus === 503
        ? t`The legal database is temporarily unavailable. Try again later.`
        : state.httpStatus === 401
          ? t`Your session has expired. Reload the page.`
          : state.httpStatus === 400
            ? t`The document address is invalid.`
            : t`Could not load the document.`;

  return (
    <>
      <div className="flex items-start gap-3 border-b px-5 py-3">
        <DialogTitle className="line-clamp-2 min-w-0 flex-1 font-sans text-sm leading-snug font-semibold">
          {loaded?.title || t`Full text`}
        </DialogTitle>
        <div className="flex shrink-0 items-center gap-0.5">
          <Button
            size="icon-sm"
            variant="ghost"
            aria-label={t`Copy text`}
            disabled={!loaded}
            onClick={copyText}
          >
            {copied ? <CheckIcon /> : <CopyIcon />}
          </Button>
          <DialogClose aria-label={t`Close`} render={<Button size="icon-sm" variant="ghost" />}>
            <XIcon />
          </DialogClose>
        </div>
      </div>
      <div className="min-h-0 flex-1 overflow-y-auto px-6 py-5">
        {state.status === "loading" ? (
          <p className="py-16 text-center text-sm text-muted-foreground">{t`Loading…`}</p>
        ) : null}
        {state.status === "failed" ? (
          <div className="flex flex-col items-center gap-3 py-16 text-center text-sm text-muted-foreground">
            <p>{failureMessage}</p>
            <Button size="sm" variant="outline" onClick={() => setAttempt((value) => value + 1)}>
              {t`Retry`}
            </Button>
          </div>
        ) : null}
        <div
          ref={contentRef}
          hidden={!loaded}
          className="mx-auto max-w-[46rem] text-[12pt] text-foreground [&_a]:text-primary [&_a]:underline"
        />
      </div>
    </>
  );
}

export function BasaDocumentLink({
  href,
  children,
  onClick,
  ...anchorProps
}: Omit<AnchorHTMLAttributes<HTMLAnchorElement>, "href"> & {
  readonly href: string;
  readonly children?: ReactNode;
}) {
  const handleClick = (event: ReactMouseEvent<HTMLAnchorElement>) => {
    onClick?.(event);
    if (
      event.defaultPrevented ||
      event.button !== 0 ||
      event.metaKey ||
      event.ctrlKey ||
      event.shiftKey ||
      event.altKey
    ) {
      return;
    }
    event.preventDefault();
    useBasaDocumentDialogStore
      .getState()
      .open({ href, quotes: collectCardQuotes(event.currentTarget) });
  };

  return (
    <a {...anchorProps} href={href} target="_blank" rel="noopener noreferrer" onClick={handleClick}>
      {children}
    </a>
  );
}

export function BasaDocumentDialog() {
  const request = useBasaDocumentDialogStore((state) => state.request);
  const close = useBasaDocumentDialogStore((state) => state.close);

  return (
    <Dialog
      open={request !== null}
      onOpenChange={(open) => {
        if (!open) close();
      }}
    >
      <DialogPopup className="h-[min(88vh,60rem)] max-w-3xl" showCloseButton={false}>
        {request ? (
          <BasaDocumentView key={request.href} href={request.href} quotes={request.quotes} />
        ) : null}
      </DialogPopup>
    </Dialog>
  );
}
